// ────────────────────────────────────────────────────────────
// API CLIENT — talks to Cloudflare Pages Functions
// ────────────────────────────────────────────────────────────

import type {
  Deal, DealFile, TitleFinding, ChainLink, MathWorksheet, TitleFlag,
  OwnershipPicture, Tract, Comp, LeadEvent, PackageDraft,
  BuyerProspect, BuyerMatch, ConversationMessage, ActivityEvent, Lens,
  InvestigationStage, WorkingTheory, OutstandingResearch, DeadEnd,
} from '../types';
import { authHeaders } from '../lib/authHeaders';

const BASE = '';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });
  if (!r.ok) {
    const text = await r.text();
    let msg = text;
    // Detect Cloudflare HTML error pages and convert to a one-line friendly message.
    if (/^\s*<!DOCTYPE/i.test(text) || /<html/i.test(text.slice(0, 200))) {
      if (r.status === 524) {
        msg = 'Request took too long (Cloudflare 524). The document may be too large — try a smaller file or split the PDF.';
      } else if (r.status === 502 || r.status === 503) {
        msg = `Cloudflare ${r.status} — backend temporarily unreachable. Try again in a moment.`;
      } else {
        msg = `Cloudflare ${r.status} error page. Server didn't respond cleanly.`;
      }
    } else {
      try { msg = JSON.parse(text).error ?? text; } catch {}
    }
    throw new Error(`API ${r.status}: ${msg}`);
  }
  return r.json() as Promise<T>;
}

export const health = {
  check(): Promise<{ ok: boolean; checks: Record<string, string>; time: string }> {
    return request('/api/health');
  },
};

export const dealsApi = {
  list(): Promise<{ deals: Deal[] }> { return request('/api/deals'); },
  save(deal: Deal): Promise<{ deal: Deal }> {
    return request('/api/deals', { method: 'POST', body: JSON.stringify(deal) });
  },
  load(id: string): Promise<DealBundle> { return request(`/api/deals/${id}`); },
  remove(id: string): Promise<{ ok: boolean }> {
    return request(`/api/deals/${id}`, { method: 'DELETE' });
  },
};

export interface DealBundle {
  deal: Deal;
  files: DealFile[];
  findings: TitleFinding[];
  chain: ChainLink[];
  math?: MathWorksheet;
  flags: TitleFlag[];
  picture?: OwnershipPicture;
  tracts: Tract[];
  comps: Comp[];
  leadEvents: LeadEvent[];
  packageDraft?: PackageDraft;
  buyerMatches: BuyerMatch[];
  conversation: ConversationMessage[];
  activity: ActivityEvent[];
  // Day 2.7 — Investigator
  investigationStages: InvestigationStage[];
  workingTheory?: WorkingTheory;
  outstandingResearch: OutstandingResearch[];
  deadEnds: DeadEnd[];
}

export type EntityKind =
  | 'file' | 'finding' | 'chain' | 'math' | 'flag' | 'picture'
  | 'tract' | 'comp' | 'leadEvent' | 'package' | 'match' | 'message' | 'activity'
  | 'research' | 'deadEnd';

export const entitiesApi = {
  upsert(dealId: string, kind: EntityKind, record: any): Promise<{ ok: boolean }> {
    return request(`/api/deals/${dealId}/entities`, {
      method: 'POST', body: JSON.stringify({ kind, record }),
    });
  },
  replaceConversation(dealId: string, messages: ConversationMessage[]): Promise<{ ok: boolean }> {
    return request(`/api/deals/${dealId}/conversation`, {
      method: 'PUT', body: JSON.stringify({ messages }),
    });
  },
};

export const filesApi = {
  downloadUrl(fileId: string): string { return `${BASE}/api/files/${fileId}`; },
  remove(fileId: string): Promise<void> {
    return request(`/api/files/${fileId}`, { method: 'DELETE' }).then(() => {});
  },
};

// ─── Claude (deal chat) ─────────────────────────────────────
export const claudeApi = {
  // Non-streaming (kept for non-chat use cases)
  chat(dealId: string, message: string, lens: Lens): Promise<{ text: string }> {
    return request('/api/claude/chat', {
      method: 'POST', body: JSON.stringify({ dealId, message, lens }),
    });
  },
  // Streaming chat. Calls onDelta(text) for each chunk; resolves with the final assistant message.
  async chatStream(
    dealId: string,
    message: string,
    lens: Lens,
    userMessageId: string | undefined,
    onDelta: (text: string) => void,
  ): Promise<{ messageId: string; fullText: string; createdAt: string }> {
    const r = await fetch(`${BASE}/api/claude/chat-stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ dealId, message, lens, userMessageId }),
    });
    if (!r.ok || !r.body) throw new Error(`Stream failed: ${r.status} ${await r.text()}`);

    const reader = r.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let final: { messageId: string; fullText: string; createdAt: string } | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split('\n\n');
      buffer = events.pop() || '';

      for (const evt of events) {
        const dataLine = evt.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;
        const payloadRaw = dataLine.slice(6).trim();
        if (!payloadRaw) continue;
        let payload: any;
        try { payload = JSON.parse(payloadRaw); } catch { continue; }

        if (payload.type === 'delta') {
          onDelta(payload.text);
        } else if (payload.type === 'done') {
          final = {
            messageId: payload.messageId,
            fullText: payload.fullText,
            createdAt: payload.createdAt,
          };
        } else if (payload.type === 'error') {
          throw new Error(payload.error);
        }
      }
    }

    if (!final) throw new Error('Stream ended without done event');
    return final;
  },


};

// ─── Package (Day 3) ─────────────────────────────────────────
export const packageApi = {
  generate(dealId: string): Promise<{ package: PackageDraft & { headlineFigure?: string; subheadline?: string }; activity: ActivityEvent }> {
    return request('/api/package/generate', {
      method: 'POST', body: JSON.stringify({ dealId }),
    });
  },
  render(dealId: string): Promise<{ pdfBlobKey: string; pdfRenderedAt: string; sizeKB: number }> {
    return request('/api/package/render', {
      method: 'POST', body: JSON.stringify({ dealId }),
    });
  },
  pdfUrl(dealId: string): string {
    return `/api/package/render?dealId=${encodeURIComponent(dealId)}`;
  },
  async uploadMap(dealId: string, file: File): Promise<{ mapBlobKey: string; mapUrl: string }> {
    const form = new FormData();
    form.append('dealId', dealId);
    form.append('file', file);
    const r = await fetch('/api/package/map', {
      method: 'POST', credentials: 'include', headers: authHeaders(), body: form,
    });
    if (!r.ok) throw new Error(`Map upload failed: ${await r.text()}`);
    return r.json();
  },
  mapUrl(dealId: string): string {
    return `/api/package/map?dealId=${encodeURIComponent(dealId)}`;
  },
  removeMap(dealId: string): Promise<void> {
    return request(`/api/package/map?dealId=${encodeURIComponent(dealId)}`, {
      method: 'DELETE',
    }).then(() => {});
  },
};

// ─── Airtable write-back (Day 5) ────────────────────────────
export interface AirtablePushSummary {
  ownersCreated: number;
  ownersUpdated: number;
  leasesCreated: number;
  leasesUpdated: number;
  compsCreated: number;
  compsUpdated: number;
  warnings: string[];
}

export const airtableApi = {
  push(dealId: string): Promise<{ summary: AirtablePushSummary }> {
    return request('/api/airtable/push', {
      method: 'POST',
      body: JSON.stringify({ dealId }),
    });
  },
};

